import React, { useState } from 'react';
import { getMarketFactoryAddress, getOracleAddress } from '../config/contracts';

/**
 * Contract Information Component
 * Displays verified contract addresses with links to Polygonscan
 * Builds trust by showing transparency and verification status
 */
function ContractInfo({ theme, compact = false }) {
  const [copiedAddress, setCopiedAddress] = useState(null);

  const contracts = [
    {
      name: 'Market Factory',
      address: getMarketFactoryAddress(),
      description: 'Creates and manages all prediction markets',
      verified: true,
      explorerUrl: `https://polygonscan.com/address/${getMarketFactoryAddress()}#code`,
    },
    {
      name: 'USDC Token',
      address: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
      description: 'Trading currency (Native USDC on Polygon)',
      verified: true,
      explorerUrl: 'https://polygonscan.com/token/0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
    },
    {
      name: 'Oracle',
      address: getOracleAddress(),
      description: 'Resolves market outcomes using Sports API',
      verified: false,
      explorerUrl: `https://polygonscan.com/address/${getOracleAddress()}`,
    },
  ];

  const copyToClipboard = (address) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  if (compact) {
    // Compact version for footer
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#00d4aa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
          <span style={{ color: theme.textSecondary }}>Verified Contracts</span>
        </div>
        
        {contracts.map((contract, index) => (
          <React.Fragment key={contract.address}>
            {index > 0 && <span style={{ color: theme.textMuted }}>•</span>}
            <a
              href={contract.explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: theme.textSecondary,
                textDecoration: 'none',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = theme.primary}
              onMouseLeave={(e) => e.currentTarget.style.color = theme.textSecondary}
            >
              <span>{contract.name}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </React.Fragment>
        ))}
      </div>
    );
  }

  // Full version for dedicated page
  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '8px', color: theme.textPrimary }}>
          Smart Contracts
        </h2>
        <p style={{ color: theme.textSecondary, fontSize: '14px' }}>
          All our contracts are verified and open-source. Click any address to view on Polygonscan.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {contracts.map((contract) => (
          <div
            key={contract.address}
            style={{
              background: theme.glassBg,
              border: theme.glassBorder,
              borderRadius: '12px',
              padding: '20px',
              backdropFilter: theme.glassBlur,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '18px', margin: 0, color: theme.textPrimary }}>
                    {contract.name}
                  </h3>
                  {contract.verified && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        background: 'rgba(0, 212, 170, 0.1)',
                        borderRadius: '6px',
                        fontSize: '11px',
                        color: '#00d4aa',
                        fontWeight: '600',
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                      Verified
                    </div>
                  )}
                </div>
                <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: theme.textSecondary }}>
                  {contract.description}
                </p>
                
                {/* Address with copy button */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <code
                    style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: theme.primary,
                      fontFamily: 'monospace',
                    }}
                  >
                    {contract.address}
                  </code>
                  
                  <button
                    onClick={() => copyToClipboard(contract.address)}
                    style={{
                      padding: '8px 12px',
                      background: copiedAddress === contract.address ? 'rgba(0, 212, 170, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '6px',
                      color: copiedAddress === contract.address ? '#00d4aa' : theme.textPrimary,
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s',
                    }}
                  >
                    {copiedAddress === contract.address ? (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Copied!
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Copy
                      </>
                    )}
                  </button>
                  
                  <a
                    href={contract.explorerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '8px 12px',
                      background: 'rgba(100, 255, 218, 0.1)',
                      border: '1px solid rgba(100, 255, 218, 0.3)',
                      borderRadius: '6px',
                      color: theme.primary,
                      fontSize: '12px',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s',
                      fontWeight: '600',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(100, 255, 218, 0.2)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(100, 255, 218, 0.1)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    View on Polygonscan
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Security Notes */}
      <div
        style={{
          marginTop: '32px',
          padding: '20px',
          background: 'rgba(100, 255, 218, 0.05)',
          border: '1px solid rgba(100, 255, 218, 0.2)',
          borderRadius: '12px',
        }}
      >
        <h3 style={{ fontSize: '16px', marginBottom: '12px', color: theme.textPrimary, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 15v2m0 0v2m0-2h2m-2 0h-2m11-5a9 9 0 11-18 0 9 9 0 0118 0z" stroke={theme.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Security & Transparency
        </h3>
        <ul style={{ margin: 0, paddingLeft: '20px', color: theme.textSecondary, fontSize: '14px', lineHeight: '1.8' }}>
          <li>The market factory is <strong>verified</strong> on Polygonscan</li>
          <li>Smart contracts are <strong>immutable</strong> - cannot be changed or upgraded</li>
          <li>Your funds are held in <strong>trustless smart contracts</strong>, not by us</li>
          <li>Questions: <a href="mailto:admin@3dragons.io" style={{ color: theme.primary }}>admin@3dragons.io</a></li>
        </ul>
      </div>
    </div>
  );
}

export default ContractInfo;

